import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { subscriptionsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const categories = [
  'Streaming',
  'Software',
  'Gaming',
  'News & Media',
  'Cloud Storage',
  'Music',
  'Fitness',
  'Food & Delivery',
  'Shopping',
  'Education',
  'Other'
];

const AddSubscription = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    currency: 'USD',
    billing_cycle: 'monthly',
    renewal_date: new Date(),
    category: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.amount || !formData.renewal_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      await subscriptionsApi.create({
        ...formData,
        amount: parseFloat(formData.amount),
        renewal_date: formData.renewal_date.toISOString()
      });
      toast.success('Subscription added successfully!');
      navigate('/subscriptions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="add-subscription-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              Add Subscription
            </h1>
            <p className="text-gray-500">Track a new subscription manually</p>
          </div>
        </div>

        {/* Form */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>
              Subscription Details
            </CardTitle>
            <CardDescription>
              Enter the details of the subscription you want to track
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Subscription Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Netflix, Spotify, Adobe Creative Cloud"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="bg-gray-50 border-gray-200 focus:bg-white"
                  data-testid="name-input"
                />
              </div>

              {/* Amount & Currency */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      placeholder="9.99"
                      value={formData.amount}
                      onChange={(e) => handleChange('amount', e.target.value)}
                      className="pl-8 bg-gray-50 border-gray-200 focus:bg-white"
                      data-testid="amount-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(value) => handleChange('currency', value)}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="CAD">CAD ($)</SelectItem>
                      <SelectItem value="AUD">AUD ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Billing Cycle & Renewal Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_cycle">Billing Cycle *</Label>
                  <Select 
                    value={formData.billing_cycle} 
                    onValueChange={(value) => handleChange('billing_cycle', value)}
                  >
                    <SelectTrigger data-testid="cycle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Next Renewal Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-gray-50 border-gray-200",
                          !formData.renewal_date && "text-muted-foreground"
                        )}
                        data-testid="date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.renewal_date ? (
                          format(formData.renewal_date, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.renewal_date}
                        onSelect={(date) => handleChange('renewal_date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => handleChange('category', value)}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this subscription..."
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  className="bg-gray-50 border-gray-200 focus:bg-white min-h-24"
                  data-testid="notes-input"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1 rounded-xl"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
                  data-testid="submit-btn"
                >
                  {isLoading ? 'Adding...' : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Subscription
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AddSubscription;
